import AutoCompleteWithTag from '@/components/AutoCompleteWithTag';
import TooltipUpdateHigherPlan from '@/components/TooltipUpgradePlan/TooltipUpdateHigherPlan';
import { Enum } from '@/constants';
import { perPageOptions } from '@/constants/options';
import { dateToTimeStamp, truncateString } from '@/helpers';
import useCountry from '@/hooks/useCountry';
import userPlans from '@/hooks/userPlans';
import { apiCaller } from '@/redux/query';
import { isSkipApiSelector } from '@/redux/slice/auth.slice';
import blockedVisitorSlice, { blockHistorySelector } from '@/redux/slice/blockedVisitor.slice';
import visitorLogSlice, {
  allAccessSelector,
  perPageSelector,
  tabsTableSelector,
  visitorLogSelector,
} from '@/redux/slice/visitorAnalytics.slice';
import { ChoiceList } from '@shopify/polaris';
import { useDispatch, useSelector } from 'react-redux';
import options from '../../options';
import { useTranslation } from 'react-i18next';
import { upperFirst } from 'lodash';

const Filters = () => {
  const { t } = useTranslation(['common', 'visitor_analytics']);
  const dispatch = useDispatch();
  const perPage = useSelector(perPageSelector);
  const allAccess = useSelector(allAccessSelector);
  const visitorLogTable = useSelector(visitorLogSelector);
  const isSkip = useSelector(isSkipApiSelector);
  const { userPlanFree } = userPlans();
  const selected = useSelector(tabsTableSelector);
  const handleCountry = useCountry();
  const dataVisitor = apiCaller.useFetchChartAnalyticsQuery(
    {
      endDate: dateToTimeStamp(allAccess.endDate),
      startDate: dateToTimeStamp(allAccess.startDate),
    },
    { skip: isSkip },
  );
  const dataBlocked = apiCaller.useFetchChartBlockQuery(
    {
      endDate: dateToTimeStamp(allAccess.endDate),
      startDate: dateToTimeStamp(allAccess.startDate),
    },
    { skip: isSkip },
  );
  const getProviders = apiCaller.useGetProvidersQuery(
    {
      endDate: dateToTimeStamp(allAccess.endDate),
      startDate: dateToTimeStamp(allAccess.startDate),
      typeList: selected === 0 ? Enum.TypeList.Allowed : selected === 1 ? Enum.TypeList.Blocked : undefined,
    },
    { skip: isSkip },
  );
  const getLocations = apiCaller.useGetLocationsQuery(
    {
      endDate: dateToTimeStamp(allAccess.endDate),
      startDate: dateToTimeStamp(allAccess.startDate),
      typeList: selected === 0 ? Enum.TypeList.Allowed : selected === 1 ? Enum.TypeList.Blocked : undefined,
    },
    { skip: isSkip },
  );
  const providerFilterAll = getProviders.data?.data?.map((item: string) => ({ label: item, value: item }));
  const locationFilterAll = getLocations.data?.data?.map((item) => ({ label: item.name, value: item.name }));
  const countryFilterVisitor = handleCountry.listCountry.filter((item) => {
    return dataVisitor?.data?.data.heatMaps.map((item) => item.countryCode).includes(item.value);
  });
  const countryFilterBlock = handleCountry.listCountry.filter((item) => {
    return dataBlocked?.data?.data.heatMaps.map((item) => item.countryCode).includes(item.value);
  });

  const handleOnChangePerPage = (selected: string[]) => {
    dispatch(visitorLogSlice.actions.handlePerPage(selected[0]));
    dispatch(
      visitorLogSlice.actions.handleVisitorLogTable({
        ...visitorLogTable,
        page: 1,
      }),
    );
    dispatch(
      blockedVisitorSlice.actions.handleBlockHistoryTable({
        ...blockHistoryTable,
        page: 1,
      }),
    );
  };

  // Allowed
  const filtersVisitors = [
    {
      key: 'perPage',
      label: t('common:per_page'),
      filter: (
        <ChoiceList
          title={t('common:per_page')}
          titleHidden
          choices={perPageOptions}
          selected={[perPage || '']}
          onChange={handleOnChangePerPage}
        />
      ),
      shortcut: true,
    },
    {
      key: 'status',
      label: t('common:status'),
      shortcut: true,
      filter: (
        <ChoiceList
          title={t('common:status')}
          titleHidden
          choices={options.statusOptions(t)}
          selected={[visitorLogTable.status || '']}
          onChange={(value) => {
            dispatch(
              visitorLogSlice.actions.handleVisitorLogTable({
                ...visitorLogTable,
                status: value[0] || undefined,
                page: 1,
              }),
            );
          }}
        />
      ),
    },
    {
      key: 'location',
      label: upperFirst(t('common:location')),
      shortcut: true,
      filter: (
        <AutoCompleteWithTag
          options={locationFilterAll || []}
          label={upperFirst(t('common:location'))}
          selectedOptions={visitorLogTable.locations || []}
          setSelectedOptions={(selected: string[]) => {
            dispatch(
              visitorLogSlice.actions.handleVisitorLogTable({
                ...visitorLogTable,
                locations: selected,
                page: 1,
              }),
            );
          }}
          allowMultiple={true}
        />
      ),
    },
    {
      key: 'countriesCode',
      label: t('common:country'),
      shortcut: true,
      filter: (
        <AutoCompleteWithTag
          options={countryFilterVisitor || []}
          label={t('common:country')}
          selectedOptions={visitorLogTable.countriesCode || []}
          setSelectedOptions={(selected: string[]) => {
            dispatch(
              visitorLogSlice.actions.handleVisitorLogTable({
                ...visitorLogTable,
                countriesCode: selected,
                page: 1,
              }),
            );
          }}
          allowMultiple={true}
        />
      ),
    },
    {
      key: 'risk',
      label: t('common:risk_level'),
      shortcut: true,
      suffix: userPlanFree ? <TooltipUpdateHigherPlan /> : undefined,
      disabled: userPlanFree,
      filter: (
        <ChoiceList
          title={t('common:risk_level')}
          titleHidden
          choices={options.riskOptions(t)}
          selected={[visitorLogTable.risk || '']}
          onChange={(value) => {
            dispatch(
              visitorLogSlice.actions.handleVisitorLogTable({
                ...visitorLogTable,
                risk: value[0] || undefined,
                page: 1,
              }),
            );
          }}
        />
      ),
    },
    {
      key: 'isp',
      label: t('common:isp'),
      shortcut: true,
      suffix: userPlanFree ? <TooltipUpdateHigherPlan /> : undefined,
      disabled: userPlanFree,
      filter: (
        <AutoCompleteWithTag
          options={providerFilterAll || []}
          label={t('common:isp')}
          selectedOptions={visitorLogTable.providers || []}
          setSelectedOptions={(selected: string[]) => {
            dispatch(
              visitorLogSlice.actions.handleVisitorLogTable({
                ...visitorLogTable,
                providers: selected,
                page: 1,
              }),
            );
          }}
          allowMultiple={true}
        />
      ),
    },
  ];

  const appliedFiltersVisitors = [
    {
      onRemove: () => {
        dispatch(visitorLogSlice.actions.handlePerPage('10'));
        dispatch(
          visitorLogSlice.actions.handleVisitorLogTable({
            ...visitorLogTable,
            page: 1,
          }),
        );
      },

      key: 'perPage',
      label: t('common:record_per_page', { perPage }),
      hidden: perPage === '10',
    },
    {
      onRemove: () =>
        dispatch(
          visitorLogSlice.actions.handleVisitorLogTable({
            ...visitorLogTable,
            status: '',
            page: 1,
          }),
        ),
      key: 'status',
      label: `${t('common:status')}: ${
        options.statusOptions(t).find((item) => item.value === visitorLogTable.status)?.label || t('common:all')
      }`,
      hidden: !visitorLogTable.status,
    },
    {
      onRemove: () =>
        dispatch(
          visitorLogSlice.actions.handleVisitorLogTable({
            ...visitorLogTable,
            risk: undefined,
            page: 1,
          }),
        ),
      key: 'risk',
      label: `${t('common:risk_level')}: ${options.riskOptions(t).find((item) => item.value === visitorLogTable.risk)?.label}`,
      hidden: !visitorLogTable.risk,
    },
    {
      onRemove: () =>
        dispatch(
          visitorLogSlice.actions.handleVisitorLogTable({
            ...visitorLogTable,
            countriesCode: [],
            page: 1,
          }),
        ),
      key: 'countriesCode',
      label: `${t('common:country')}: ${truncateString(
        countryFilterVisitor
          .filter((item) => visitorLogTable.countriesCode?.includes(item.value))
          ?.map((item) => item.label)
          .toString(),
        100,
      )}`,
      hidden: (visitorLogTable.countriesCode || '').length === 0,
    },
    {
      onRemove: () =>
        dispatch(
          visitorLogSlice.actions.handleVisitorLogTable({
            ...visitorLogTable,
            providers: [],
            page: 1,
          }),
        ),
      key: 'isp',
      label: `${t('common:isp')}: ${truncateString(
        (providerFilterAll || [])
          .filter((item) => visitorLogTable.providers?.includes(item.value))
          ?.map((item) => item.label)
          .toString(),
        100,
      )}`,
      hidden: (visitorLogTable.providers || '').length === 0,
    },
    {
      onRemove: () =>
        dispatch(
          visitorLogSlice.actions.handleVisitorLogTable({
            ...visitorLogTable,
            locations: [],
            page: 1,
          }),
        ),
      key: 'location',
      label: `${upperFirst(t('common:location'))}: ${truncateString(
        (locationFilterAll || [])
          .filter((item) => visitorLogTable.locations?.includes(item.value))
          ?.map((item) => item.label)
          .toString(),
        100,
      )}`,
      hidden: (visitorLogTable.locations || '').length === 0,
    },
  ];

  // Blocked
  const blockHistoryTable = useSelector(blockHistorySelector);
  const filtersBlocked = [
    {
      key: 'perPage',
      label: t('common:per_page'),
      filter: (
        <ChoiceList
          title={t('common:per_page')}
          titleHidden
          choices={perPageOptions}
          selected={[perPage]}
          onChange={handleOnChangePerPage}
        />
      ),
      shortcut: true,
    },
    {
      key: 'country',
      label: t('common:country'),
      filter: (
        <AutoCompleteWithTag
          options={countryFilterBlock}
          label={t('common:country')}
          selectedOptions={blockHistoryTable.countriesCode || []}
          setSelectedOptions={(selected: string[]) => {
            dispatch(
              blockedVisitorSlice.actions.handleBlockHistoryTable({
                ...blockHistoryTable,
                countriesCode: selected,
                page: 1,
              }),
            );
          }}
          allowMultiple={true}
        />
      ),
      shortcut: true,
    },
    {
      key: 'location',
      label: upperFirst(t('common:location')),
      shortcut: true,
      filter: (
        <AutoCompleteWithTag
          options={locationFilterAll || []}
          label={upperFirst(t('common:location'))}
          selectedOptions={blockHistoryTable.locations || []}
          setSelectedOptions={(selected: string[]) => {
            dispatch(
              blockedVisitorSlice.actions.handleBlockHistoryTable({
                ...blockHistoryTable,
                locations: selected,
                page: 1,
              }),
            );
          }}
          allowMultiple={true}
        />
      ),
    },
    {
      key: 'isp',
      label: t('common:isp'),
      suffix: userPlanFree ? <TooltipUpdateHigherPlan /> : undefined,
      disabled: userPlanFree,
      filter: (
        <AutoCompleteWithTag
          options={providerFilterAll || []}
          label={t('common:isp')}
          selectedOptions={blockHistoryTable.providers || []}
          setSelectedOptions={(selected: string[]) => {
            dispatch(
              blockedVisitorSlice.actions.handleBlockHistoryTable({
                ...blockHistoryTable,
                providers: selected,
                page: 1,
              }),
            );
          }}
          allowMultiple={true}
        />
      ),
      shortcut: true,
    },
    {
      key: 'risk',
      label: t('common:risk_level'),
      shortcut: true,
      suffix: userPlanFree ? <TooltipUpdateHigherPlan /> : undefined,
      disabled: userPlanFree,
      filter: (
        <ChoiceList
          title={t('common:risk_level')}
          titleHidden
          choices={options.riskOptions(t)}
          selected={[blockHistoryTable.risk || '']}
          onChange={(value) => {
            dispatch(
              blockedVisitorSlice.actions.handleBlockHistoryTable({
                ...blockHistoryTable,
                risk: value[0] || undefined,
                page: 1,
              }),
            );
          }}
        />
      ),
    },
  ];

  const appliedFiltersBlocked = [
    {
      onRemove: () => {
        dispatch(visitorLogSlice.actions.handlePerPage('10'));
        dispatch(
          blockedVisitorSlice.actions.handleBlockHistoryTable({
            ...blockHistoryTable,
            page: 1,
          }),
        );
      },
      key: 'perPage',
      label: t('common:record_per_page', { perPage }),
      hidden: perPage === '10',
    },
    {
      onRemove: () =>
        dispatch(
          blockedVisitorSlice.actions.handleBlockHistoryTable({
            ...blockHistoryTable,
            countriesCode: [],
            page: 1,
          }),
        ),
      key: 'country',
      label: `${t('common:country')}: ${truncateString(
        countryFilterBlock
          .filter((item) => blockHistoryTable.countriesCode?.includes(item.value))
          ?.map((item) => item.label)
          .toString(),
        100,
      )}`,
      hidden: (blockHistoryTable.countriesCode || '').length === 0,
    },
    {
      onRemove: () =>
        dispatch(
          blockedVisitorSlice.actions.handleBlockHistoryTable({
            ...blockHistoryTable,
            providers: undefined,
            page: 1,
          }),
        ),
      key: 'isp',
      label: `${t('common:isp')}: ${truncateString(
        (providerFilterAll || [])
          .filter((item) => blockHistoryTable.providers?.includes(item.value))
          ?.map((item) => item.label)
          .toString(),
        100,
      )}`,
      hidden: (blockHistoryTable.providers || '').length === 0,
    },
    {
      onRemove: () =>
        dispatch(
          blockedVisitorSlice.actions.handleBlockHistoryTable({
            ...blockHistoryTable,
            locations: [],
            page: 1,
          }),
        ),
      key: 'location',
      label: `${upperFirst(t('common:location'))}: ${truncateString(
        (locationFilterAll || [])
          .filter((item) => blockHistoryTable.locations?.includes(item.value))
          ?.map((item) => item.label)
          .toString(),
        100,
      )}`,
      hidden: (blockHistoryTable.locations || '').length === 0,
    },
    {
      onRemove: () =>
        dispatch(
          blockedVisitorSlice.actions.handleBlockHistoryTable({
            ...blockHistoryTable,
            risk: undefined,
            page: 1,
          }),
        ),
      key: 'risk',
      label: `${t('common:risk_level')}: ${options.riskOptions(t).find((item) => item.value === blockHistoryTable.risk)?.label}`,
      hidden: !blockHistoryTable.risk,
    },
  ];

  return {
    visitors: {
      filters: filtersVisitors,
      appliedFilters: appliedFiltersVisitors,
    },
    Blocked: {
      filters: filtersBlocked,
      appliedFilters: appliedFiltersBlocked,
    },
  };
};

export default Filters;
